import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  DatePicker,
  Typography,
  Statistic,
  Row,
  Col,
  Tabs,
  Badge,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMaterialRequests } from '../../hooks/useMaterialRequest';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useWarehouses } from '../../hooks/useInventory';
import { useAuthStore } from '@/store/authStore';
import { canAccessRouteByPermissions } from '@/config/routePermissions';
import { isPlatformSuperAdmin } from '@/utils/authUser';
import type { MaterialRequest } from '../../services/materialRequest.service';

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

export const MaterialRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const user = useAuthStore((s) => s.user);
  const isSuper = isPlatformSuperAdmin(user);
  const perms = user?.permissions ?? [];
  const canCreate = canAccessRouteByPermissions(
    '/scm/inventory/material-requests/new',
    perms,
    isSuper
  );
  const canEdit = canAccessRouteByPermissions(
    '/scm/inventory/material-requests/:id/edit',
    perms,
    isSuper
  ) || perms.includes('inventory.material_request.edit');

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [activeTab, setActiveTab] = useState('draft');

  const { data: warehouses = [] } = useWarehouses();

  const { data: allRequests = [], isLoading } = useMaterialRequests({
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    startDate: dateRange?.[0],
    endDate: dateRange?.[1],
  });

  const filteredRequests = allRequests
    .filter((request) => {
      if (activeTab === 'draft') {
        return request.request_status === 'Draft';
      }
      if (activeTab === 'awaiting-approval') {
        return request.approval_status === 'Pending' || request.request_status === 'Pending';
      }
      if (activeTab === 'approved-open') {
        return ['Approved', 'Partially Issued'].includes(request.request_status);
      }
      if (activeTab === 'completed') {
        return ['Fully Issued', 'Closed', 'Cancelled', 'Rejected'].includes(request.request_status);
      }
      return true;
    })
    .filter((request) => {
      if (!searchText) return true;
      const q = searchText.toLowerCase();
      return (
        request.request_number.toLowerCase().includes(q) ||
        (request.work_order_number && request.work_order_number.toLowerCase().includes(q)) ||
        (request.context_label && request.context_label.toLowerCase().includes(q)) ||
        (request.project_summary?.project_number &&
          request.project_summary.project_number.toLowerCase().includes(q))
      );
    });

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      Draft: 'default',
      Pending: 'warning',
      Approved: 'success',
      Rejected: 'error',
      'Partially Issued': 'processing',
      'Fully Issued': 'success',
      Closed: 'default',
      Cancelled: 'error',
    };
    return colorMap[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colorMap: Record<string, string> = {
      Low: 'blue',
      Normal: 'default',
      High: 'orange',
      Urgent: 'red',
    };
    return colorMap[priority] || 'default';
  };

  const columns: ColumnsType<MaterialRequest> = [
    {
      title: 'Request #',
      dataIndex: 'request_number',
      key: 'request_number',
      width: 140,
      fixed: 'left',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Context',
      key: 'context',
      width: 170,
      render: (_: unknown, record) => {
        if (record.work_order_id && record.work_order_number) {
          return (
            <Link to={`/mfg/work-orders/${record.work_order_id}`}>{record.work_order_number}</Link>
          );
        }
        if (record.project_id && record.project_summary) {
          return (
            <Text>
              {record.project_summary.project_number || record.project_summary.project_name}
            </Text>
          );
        }
        return <Text type="secondary">{record.context_label || '—'}</Text>;
      },
    },
    {
      title: 'Type',
      dataIndex: 'request_type',
      key: 'request_type',
      width: 90,
      render: (type: string) => <Tag color={type === 'BOM' ? 'blue' : 'green'}>{type}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'request_status',
      key: 'request_status',
      width: 130,
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: (priority: string) => <Tag color={getPriorityColor(priority)}>{priority}</Tag>,
    },
    {
      title: 'Items',
      key: 'items',
      width: 70,
      render: (_: unknown, record) => record.items?.length ?? 0,
    },
    {
      title: 'Est. cost',
      dataIndex: 'total_estimated_cost',
      key: 'total_estimated_cost',
      width: 110,
      render: (cost: number | string) => {
        const n = typeof cost === 'string' ? parseFloat(cost) : Number(cost || 0);
        return formatCurrency(Number.isFinite(n) ? n : 0);
      },
    },
    {
      title: 'Request date',
      dataIndex: 'request_date',
      key: 'request_date',
      width: 110,
      render: (date: string) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Required',
      dataIndex: 'required_date',
      key: 'required_date',
      width: 110,
      render: (date: string) => (date ? dayjs(date).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Warehouse',
      dataIndex: 'source_warehouse_id',
      key: 'source_warehouse_id',
      width: 120,
      ellipsis: true,
      render: (id: string) => {
        const wh = warehouses.find((w) => w.id === id);
        return wh?.warehouse_name || wh?.warehouse_code || id || '—';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 160,
      render: (_: unknown, record) => (
        <Space size={0}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/scm/inventory/material-requests/${record.id}`)}
          >
            View
          </Button>
          {canEdit && record.request_status === 'Draft' ? (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/scm/inventory/material-requests/${record.id}/edit`)}
            >
              Edit
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const stats = {
    draft: allRequests.filter((r) => r.request_status === 'Draft').length,
    awaitingApproval: allRequests.filter(
      (r) => r.approval_status === 'Pending' || r.request_status === 'Pending'
    ).length,
    approvedOpen: allRequests.filter((r) =>
      ['Approved', 'Partially Issued'].includes(r.request_status)
    ).length,
    completed: allRequests.filter((r) =>
      ['Fully Issued', 'Closed', 'Cancelled', 'Rejected'].includes(r.request_status)
    ).length,
    total: allRequests.length,
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <Title level={2}>Material Requests</Title>
          <Text type="secondary">Request materials for work orders, projects, or workshop cases</Text>
        </div>
        {canCreate ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/scm/inventory/material-requests/new')}
          >
            New request
          </Button>
        ) : null}
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Draft" value={stats.draft} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Awaiting approval" value={stats.awaitingApproval} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Approved (open)" value={stats.approvedOpen} valueStyle={{ color: '#145A54' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Total" value={stats.total} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Search
            placeholder="Search request, WO, project"
            allowClear
            style={{ width: 280 }}
            onSearch={setSearchText}
            onChange={(e) => !e.target.value && setSearchText('')}
          />
          <Select placeholder="Status" allowClear style={{ width: 150 }} onChange={(v) => setStatusFilter(v || '')}>
            <Select.Option value="Draft">Draft</Select.Option>
            <Select.Option value="Pending">Pending</Select.Option>
            <Select.Option value="Approved">Approved</Select.Option>
            <Select.Option value="Partially Issued">Partially Issued</Select.Option>
            <Select.Option value="Fully Issued">Fully Issued</Select.Option>
            <Select.Option value="Closed">Closed</Select.Option>
            <Select.Option value="Cancelled">Cancelled</Select.Option>
          </Select>
          <Select placeholder="Priority" allowClear style={{ width: 130 }} onChange={(v) => setPriorityFilter(v || '')}>
            <Select.Option value="Low">Low</Select.Option>
            <Select.Option value="Normal">Normal</Select.Option>
            <Select.Option value="High">High</Select.Option>
            <Select.Option value="Urgent">Urgent</Select.Option>
          </Select>
          <RangePicker
            onChange={(dates) => {
              if (dates) {
                setDateRange([
                  dates[0]?.format('YYYY-MM-DD') || '',
                  dates[1]?.format('YYYY-MM-DD') || '',
                ]);
              } else {
                setDateRange(null);
              }
            }}
          />
          <Button
            icon={<FilterOutlined />}
            onClick={() => {
              setSearchText('');
              setStatusFilter('');
              setPriorityFilter('');
              setDateRange(null);
            }}
          >
            Clear
          </Button>
        </Space>
      </Card>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'draft',
              label: (
                <Badge count={stats.draft} offset={[10, 0]}>
                  <span>Draft</span>
                </Badge>
              ),
            },
            {
              key: 'awaiting-approval',
              label: (
                <Badge count={stats.awaitingApproval} offset={[10, 0]}>
                  <span>Awaiting approval</span>
                </Badge>
              ),
            },
            {
              key: 'approved-open',
              label: (
                <Badge count={stats.approvedOpen} offset={[10, 0]}>
                  <span>Approved (open)</span>
                </Badge>
              ),
            },
            {
              key: 'completed',
              label: <span>Completed</span>,
            },
            {
              key: 'all',
              label: <span>All</span>,
            },
          ]}
        />

        <Table
          columns={columns}
          dataSource={filteredRequests}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1500 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} requests`,
          }}
        />
      </Card>
    </div>
  );
};

export default MaterialRequestsPage;
